import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';
import trackQuickPickButtonClicked from '../../../../base/trackers/helpers';

import DivButton from 'components/common/DivButton';
import { LineProps } from 'components/play/Types/Types';
import { PRIMARY_NUMBERS, RANGE_FROM, RANGE_TO, toggleNumber } from 'components/play/helpers/linesHelper';
import useLinesHelper from 'components/play/helpers/useLinesHelper';
import { RootState } from 'components/play/redux/store';
import { rangeArray } from 'utils/helpers';

type Props = Readonly<{ selectSignal: { number?: number }; checked: boolean }> & LineProps;

export default function NumberPanel({ selectSignal, lineNumbers, lineIndex, checked }: Props) {
    const { t } = useTranslation();
    const { numbersToPlay } = useSelector((state: RootState) => state.kenoPlayStore);
    const { lottery, setLine, setRandomLine, removeLine, ranges } = useLinesHelper();
    const [show, setShow] = useState<boolean>();

    useEffect(() => setShow(selectSignal.number != null), [selectSignal]);

    const handleBallClick = (targetNumber: number) =>
        setLine(lineIndex, [toggleNumber(lineNumbers[PRIMARY_NUMBERS], targetNumber, numbersToPlay)]);

    const baseView = () => (
        <Modal show={show} className="keno-play keno modals" onHide={() => setShow(false)}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('playPicker.keno.pickNumbersWithinRange', {
                        from: ranges[PRIMARY_NUMBERS][RANGE_FROM],
                        to: ranges[PRIMARY_NUMBERS][RANGE_TO],
                    })}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row justify-content-center">
                    <button
                        type="button"
                        onClick={() => {
                            setRandomLine(lineIndex);
                            trackQuickPickButtonClicked(lottery.slug);
                        }}
                        className="btn btn-sm btn-primary quick-pick-mobile mx-2"
                    >
                        {t('playPicker.quickPick')}
                    </button>
                    <button
                        type="button"
                        onClick={() => removeLine(lineIndex)}
                        className="btn btn-sm btn-secondary purchase-line-clear-mobile mx-2"
                    >
                        {t('playPicker.clear')}
                    </button>
                </div>
                <div className="row justify-content-center">
                    {rangeArray(ranges[PRIMARY_NUMBERS][RANGE_FROM], ranges[PRIMARY_NUMBERS][RANGE_TO]).map((i) => {
                        const selected = lineNumbers[PRIMARY_NUMBERS]?.includes(i);
                        const active = selected || lineNumbers[PRIMARY_NUMBERS].length < numbersToPlay;

                        return (
                            <div key={i} className={`ball_wrapper ${!active ? 'ball_disabled' : ''}`}>
                                <DivButton
                                    onClick={() => handleBallClick(i)}
                                    className={`${selected ? 'ball_selected' : ''} ball`}
                                >
                                    {i}
                                </DivButton>
                            </div>
                        );
                    })}
                </div>
                <div className="btn-pick">
                    <button
                        type="button"
                        onClick={() => setShow(false)}
                        className={`btn btn-primary btn-submit mb-0 ${checked ? '' : 'disabled'}`}
                    >
                        {t('playPicker.lottery.addThisTicket')}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );

    const defaultView = () => (
        <Modal show={show} className="play-picker-modal" onHide={() => setShow(false)} centered>
            <Modal.Header closeButton />
            <Modal.Body>
                <div className={`play-picker ${checked ? 'checked' : ''}`}>
                    <div className="row-buttons no-gutters d-flex justify-content-center">
                        <button
                            type="button"
                            onClick={() => {
                                setRandomLine(lineIndex);
                                trackQuickPickButtonClicked(lottery.slug);
                            }}
                            className="btn btn-secondary quick-pick"
                        >
                            {t('playPicker.quickPick')}
                        </button>

                        <button
                            type="button"
                            onClick={() => removeLine(lineIndex)}
                            className="btn btn-secondary purchase-line-clear"
                        >
                            {t('playPicker.clear')}
                        </button>
                    </div>
                    <div className="pick-info d-flex justify-content-center">
                        {lineNumbers[PRIMARY_NUMBERS].length === numbersToPlay && (
                            <i className="fas fa-check-circle" />
                        )}
                        <span>{`Pick ${[numbersToPlay]} numbers`}</span>
                    </div>
                    <div className="purchase-line">
                        <div className="purchase-line-numbers">
                            {rangeArray(ranges[PRIMARY_NUMBERS][RANGE_FROM], ranges[PRIMARY_NUMBERS][RANGE_TO]).map(
                                (i) => {
                                    const selected = lineNumbers[PRIMARY_NUMBERS]?.includes(i);
                                    return (
                                        <DivButton
                                            onClick={() => handleBallClick(i)}
                                            className={`number text-center purchase-line-number d-flex align-items-center justify-content-center purchase-line-to-pick ${
                                                selected ? 'picked' : ''
                                            }`}
                                        >
                                            <i className="fas fa-times" />
                                            {i}
                                        </DivButton>
                                    );
                                }
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-primary mx-auto" onClick={() => setShow(false)}>
                    {t('playPicker.modal.done')}
                </button>
            </Modal.Footer>
        </Modal>
    );
    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}
